// Ekran 06 — Üretim Beyanı API istemcisi.

import { api } from '../../lib/api'

export type BeyanGunuDurumu = 'taslak' | 'onayli'
export type TesisBeyanTipi = 'uretim' | 'ic_ihtiyac' | 'satis' | 'gaz_tuketim' | 'taahhut'
export type OlayTipi = 'mesale' | 'durus' | 'kesinti' | 'parti' | 'sevk' | 'calisma'
export type Bant = 'saglikli' | 'izle' | 'kritik'

/** null hücre = o saatte hücre YOK (sıklık açmıyor); boş hücreden farklıdır. */
export type HucreDto = {
  deger: number | null
  durum: 'dolu' | 'eksik' | 'bekleniyor'
  bant: Bant | null
}

export type BeyanSatiri = {
  parametreId: string
  ad: string
  birim: string | null
  frek: string
  frekEtiket: string
  kaynak: 'manuel' | 'lab' | 'olay'
  sira: number
  hucreler: (HucreDto | null)[]
  eksikSaatler: number[]
  toplam: number | null
  /** DOLU hücrelerin ortalaması; hiç dolu hücre yoksa null. */
  ortalama: number | null
  /** ref:644 — Toplam sütunu toplam yerine ORTALAMA göstersin mi? */
  toplamOrtalama: boolean
  /** Referansın satır `d` alanı; birimden tahmin edilemez (bkz. BeyanOkuma.Basamak). */
  basamak: number
  gosterge: string | null
  /** K23 olay satırının olay tipi — grid kayıtları bununla süzüyor. */
  olayTipi: OlayTipi | null
}

export type BeyanUnitesi = {
  uniteId: string
  kod: string
  ad: string
  grup: 'on' | 'cur' | 'depo' | 'gaz' | 'enerji'
  durum: 'aktif' | 'bakimda' | 'pasif'
  satirlar: BeyanSatiri[]
  /** K30: eksik + eşik dışı hücre sayısı. */
  pip: number
}

export type TesisSatiri = {
  tip: TesisBeyanTipi
  etiket: string
  birim: string
  hucreler: (HucreDto | null)[]
  eksikSaatler: number[]
  hesaplanan: boolean
  toplam: number | null
  hesaplanabilir: boolean
  /** K28: aynı tipin ünite parametrelerinden gelen gün toplamı ("ünite Σ"). */
  uniteToplami: number | null
}

export type OlayDto = {
  id: string
  uniteId: string | null
  uniteKod: string | null
  tip: OlayTipi
  saat: number
  deger: number | null
  birim: string | null
  aciklama: string | null
}

export type BeyanGunuDto = {
  tarih: string
  durum: BeyanGunuDurumu
  onayZamani: string | null
  onayKullanici: string | null
  /** K30: bu saate kadarki hücreler bekleniyor. */
  sinirSaat: number
  uniteler: BeyanUnitesi[]
  tesis: TesisSatiri[]
  dengesizlik: (number | null)[]
  /** saglikli · izle · kritik — ayar_esik'ten (§6.3). */
  dengesizlikBant: (string | null)[]
  taahhutSapmasi: (number | null)[]
  olaylar: OlayDto[]
  toplamPip: number
  /** K29 bandının ayar_esik sınırları — panelin tolerans dipnotu buradan yazılır. */
  dengesizlikAmber: number
  dengesizlikKirmizi: number
  /**
   * Görüntülenen gün BUGÜNSE içinde bulunulan saat (0–23), değilse null.
   * Grid o sütunu koyu kırmızı çerçeveyle işaretliyor.
   *
   * `sinirSaat`ten ayrı: sınır geçmiş günde 24, gelecek günde 0'dır — ikisi de
   * "şu anki saat" değil. Saat sunucudan geliyor (§12 Europe/Istanbul).
   */
  aktifSaat: number | null
}

/**
 * Gün seçicisinin satırı (ref:383-386). `var:false` = günün beyan kaydı henüz
 * açılmamış; seçilebilir ama boş grid gelir.
 */
export type BeyanGunOzeti = {
  tarih: string
  durum: BeyanGunuDurumu
  var: boolean
}

export type MutabakatKarti = {
  baslik: string
  kuralRef: string
  sol: number | null
  solEtiket: string
  sag: number | null
  sagEtiket: string
  farkYuzde: number | null
  tolerans: number | null
  bant: Bant | 'hesaplanamadi'
  not: string | null
  /** K24 gaz dengesi kartında üretilen − tüketilen − meşale; diğerlerinde null. */
  depoDegisimi: number | null
}

export type MutabakatDto = {
  tarih: string
  besleme: MutabakatKarti
  elektrik: MutabakatKarti
  gazDengesi: MutabakatKarti
}

export type EksikHucre = {
  parametreId: string
  uniteKod: string
  ad: string
  saatler: number[]
}

export const beyan = {
  /**
   * Gün seçicisinin listesi — `bitis` gününden geriye `gun` adet.
   * `bitis` verilmezse sunucu BUGÜNden geriye sayar (Zaman.BugunTr).
   */
  gunler: (bitis?: string, gun = 7) =>
    api<BeyanGunOzeti[]>(
      `/api/beyan/gunler?gun=${gun}${bitis ? `&bitis=${bitis}` : ''}`,
    ),

  gun: (tarih: string) => api<BeyanGunuDto>(`/api/beyan/${tarih}`),

  mutabakat: (tarih: string) => api<MutabakatDto>(`/api/beyan/${tarih}/mutabakat`),

  degerler: (tarih: string, girdiler: { parametreId: string; saat: number; deger: number | null }[]) =>
    api<BeyanGunuDto>(`/api/beyan/${tarih}/degerler`, { yontem: 'PUT', govde: girdiler }),

  tesis: (tarih: string, girdiler: { tip: TesisBeyanTipi; saat: number; deger: number | null }[]) =>
    api<BeyanGunuDto>(`/api/beyan/${tarih}/tesis`, { yontem: 'PUT', govde: girdiler }),

  hesapla: (tarih: string, tip: TesisBeyanTipi) =>
    api<BeyanGunuDto>(`/api/beyan/${tarih}/tesis/hesapla`, { yontem: 'POST', govde: { tip } }),

  olay: (tarih: string, govde: {
    uniteId: string | null
    tip: OlayTipi
    saat: number
    deger: number | null
    birim: string | null
    aciklama: string | null
  }) => api<BeyanGunuDto>(`/api/beyan/${tarih}/olay`, { yontem: 'POST', govde }),

  dunuKopyala: (tarih: string) =>
    api<{ kopyalanan: number; gun: BeyanGunuDto; mesaj: string }>(
      `/api/beyan/${tarih}/dunu-kopyala`, { yontem: 'POST' }),

  onayla: (tarih: string) =>
    api<{ gun: BeyanGunuDto; mesaj: string }>(`/api/beyan/${tarih}/onayla`, { yontem: 'POST' }),
}
